/**
 * "Chiedi a mia Tina" (WIZARD-WIREFLOW §1 regola 8): una domanda libera senza uscire dal
 * wizard. Passa da POST /chat/turns; finche la lane A non lo implementa il server risponde
 * 501 e qui si dice con parole sue che la risposta arrivera piu avanti — senza inventarla.
 */
import { useState } from 'react'

import { AnswerField, clearDraft, ErrorMessage, PrimaryButton, SecondaryButton } from '../components'
import type { ApiError } from '@mamma/contracts'
import { ApiFailure } from '../api/client'
import { wizardApi } from '../api/wizard'

const NON_ANCORA =
  'Questa parte non è ancora attiva: la sua domanda non è andata persa, ma per ora non so risponderle. ' +
  'Può continuare da dove eravamo.'

export type AskAgentPanelProps = {
  label: string
  /** Testo del pulsante di chiusura: nel wizard "Torno alla domanda", in Oggi "Chiudi". */
  closeLabel?: string
  /** Prima battuta di mia (DEC-21): mostrata sopra il campo, lei risponde lì. */
  opening?: string
  onClose: () => void
}

export function AskAgentPanel({ label, closeLabel = 'Torno alla domanda', opening, onClose }: AskAgentPanelProps) {
  const [testo, setTesto] = useState('')
  // L'apertura vale per il primo messaggio: dopo la risposta di mia si torna a "Chiedo ancora".
  const [apertura, setApertura] = useState(opening)
  const [risposta, setRisposta] = useState<string | null>(null)
  const [errore, setErrore] = useState<ApiError | null>(null)
  const [busy, setBusy] = useState(false)

  const chiedi = async () => {
    if (!testo.trim()) return
    setBusy(true)
    setErrore(null)
    try {
      const esito = await wizardApi.askAgent(testo.trim(), apertura)
      setApertura(undefined)
      setRisposta(esito.reply_text ?? NON_ANCORA)
      clearDraft('chiedi-all-agente')
      setTesto('')
    } catch (problema) {
      if (problema instanceof ApiFailure && problema.status === 501) setRisposta(NON_ANCORA)
      else if (problema instanceof ApiFailure) setErrore(problema.error)
      else setErrore({ code: 'imprevisto', message_it: NON_ANCORA, remedy_it: 'Tocchi Chiudi e continui.' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-end bg-[rgba(43,45,49,0.35)]">
      <div className="mx-auto w-full max-w-[430px]">
        <div
          role="dialog"
          aria-modal="true"
          aria-label={label}
          className="surface-card rounded-t-card px-[22px] pb-[22px] pt-[24px]"
        >
          <h2 className="text-band font-bold text-ink">{label}</h2>
          {risposta ? (
            <>
              <p className="mt-3 text-body text-ink">{risposta}</p>
              <PrimaryButton onClick={() => setRisposta(null)}>Chiedo ancora</PrimaryButton>
            </>
          ) : (
            <>
              {apertura && (
                <p className="mt-3 text-body text-ink" data-testid="apertura-mia">
                  {apertura}
                </p>
              )}
              <AnswerField
                label={apertura ? 'Scriva la sua risposta' : 'Scriva la sua domanda'}
                draftKey="chiedi-all-agente"
                disabled={busy}
                onChange={setTesto}
              />
              {errore && <ErrorMessage error={errore} onRetry={() => void chiedi()} />}
              <PrimaryButton disabled={busy || !testo.trim()} onClick={() => void chiedi()}>
                {apertura ? 'Invia' : 'Chiedi'}
              </PrimaryButton>
            </>
          )}
          <div className="mt-3">
            <SecondaryButton onClick={onClose}>{closeLabel}</SecondaryButton>
          </div>
        </div>
      </div>
    </div>
  )
}
